import { create } from 'zustand';
import { Match } from '../types';
import apiService from '../services/api';

interface MatchStore {
  matches: Match[];
  selectedMatch: Match | null;
  loading: boolean;
  error: string | null;
  filters: {
    status: string;
    tournamentId?: number;
    dateFrom?: string;
    dateTo?: string;
  };
  setMatches: (matches: Match[]) => void;
  setSelectedMatch: (match: Match | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setFilters: (filters: Partial<MatchStore['filters']>) => void;
  fetchMatches: (params?: {
    status?: string;
    date_from?: string;
    date_to?: string;
    tournament_id?: number;
    is_tournament_match?: boolean;
    limit?: number;
  }) => Promise<void>;
  addMatch: (matchData: any) => Promise<void>;
  updateMatch: (matchId: number, matchData: any) => Promise<void>;
  deleteMatch: (matchId: number) => Promise<void>;
}

export const useMatchStore = create<MatchStore>((set, get) => ({
  matches: [],
  selectedMatch: null,
  loading: false,
  error: null,
  filters: {
    status: 'all',
  },
  setMatches: (matches) => set({ matches }),
  setSelectedMatch: (match) => set({ selectedMatch: match }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),
  setFilters: (filters) => set((state) => ({ filters: { ...state.filters, ...filters } })),
  fetchMatches: async (params?: {
    status?: string;
    date_from?: string;
    date_to?: string;
    tournament_id?: number;
    is_tournament_match?: boolean;
    limit?: number;
  }) => {
    set({ loading: true, error: null });
    try {
      const response = await apiService.getAllMatches(params);
      // Handle both array and object with data property
      const data = response.data as any;
      const matchesData = Array.isArray(data) 
        ? data 
        : (data?.data || data || []);
      set({ matches: matchesData, loading: false });
    } catch (error: any) {
      set({ error: error.response?.data?.detail || 'Failed to fetch matches', loading: false });
    }
  },
  addMatch: async (matchData: any) => {
    set({ loading: true, error: null });
    try {
      const response = await apiService.createMatch(matchData);
      set((state) => ({ matches: [...state.matches, response.data], loading: false }));
    } catch (error: any) {
      set({ error: error.response?.data?.detail || 'Failed to create match', loading: false });
      throw error;
    }
  },
  updateMatch: async (matchId: number, matchData: any) => {
    set({ loading: true, error: null });
    try {
      const response = await apiService.updateMatch(matchId, matchData);
      set((state) => ({
        matches: state.matches.map((m) => (m.id === matchId ? response.data : m)),
        loading: false,
      }));
    } catch (error: any) {
      set({ error: error.response?.data?.detail || 'Failed to update match', loading: false });
      throw error;
    }
  },
  deleteMatch: async (matchId: number) => {
    set({ loading: true, error: null });
    try {
      await apiService.deleteMatch(matchId);
      set((state) => ({
        matches: state.matches.filter((m) => m.id !== matchId),
        loading: false,
      }));
    } catch (error: any) {
      set({ error: error.response?.data?.detail || 'Failed to delete match', loading: false });
      throw error;
    }
  },
}));

